import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { AlertContent, ModalFactory, ModalText } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getSitePaymentsRequest, selectPaymentSetsForSite } from 'store/Collections/Payments';
import { getSitePayoutsRequest, Payouts, selectPayouts } from 'store/Collections/Payouts';
import { PayPeriods, updatePayPeriodStatusRequest } from 'store/Collections/PayPeriods';
import { IStore } from 'store/types';
import { ApiQueryOperator, IPayPeriod, IPayPeriodClosure, ISite, PayoutStatus, PayPeriodAction } from 'types';
import { payPeriodDateFilters } from 'utils/payPeriods';
import ConfirmStep from './ConfirmStep';
import ReviewStep from './ReviewStep';
import { buildReviewDays, groupOutstandingPayouts } from './utils';

const CONFIRM = { maxWidth: 760, title: 'Close Pay Period', leftActionLabel: 'Back', rightActionLabel: 'Close Period' };
const STEP = {
  reviewPaymentSets: { maxWidth: 908, title: 'Review Before Closing Pay Period', leftActionLabel: 'Cancel', rightActionLabel: 'Next' },
  reviewRequired: { maxWidth: 831, title: 'Review Required', rightActionLabel: 'Cancel', rightActionAppearance: 'white' as const, hideLeftAction: true },
  confirm: CONFIRM,
  converting: { ...CONFIRM, title: 'Close Pay Period - Converting to Payroll' },
};

// The period's pending, expired and payroll payouts of every method, since payroll ones can await review too.
const openPayoutsInPeriod = (payPeriod: IPayPeriod) => [...payPeriodDateFilters(payPeriod), { key: 'status', operator: ApiQueryOperator.in, value: [PayoutStatus.PENDING, PayoutStatus.EXPIRED, PayoutStatus.PAYROLL] }];

// Fits the viewport so a step's list scrolls under its pinned header while the title and buttons stay in view.
// The frames draw both buttons at a fixed width rather than hugging their labels.
const Content = styled(AlertContent)`
  max-height: calc(100vh - ${toRem(2 * theme.space['4xl'])});

  > section {
    min-height: 0;
  }

  ${ModalText} {
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
  }

  footer > button {
    min-width: ${toRem(178)};
  }
`;

interface IClosePayPeriodModalProps {
  site: ISite;
  orgId: string;
  payPeriod: IPayPeriod;
  // Today in the site's timezone as YYYY-MM-DD; decides which review days are still upcoming.
  today: string;
  // Called on dismiss; carries the closure when the period was closed.
  onClose: (closure?: IPayPeriodClosure) => void;
  dataCy?: string;
}

// Review every business day, then confirm the close or list what blocks it. The saga toasts the outcome; the modal dismisses either way.
const ClosePayPeriodModal = ({ site, orgId, payPeriod, today, onClose, dataCy = 'close-pay-period-modal' }: IClosePayPeriodModalProps) => {
  const dispatch = useDispatch();
  const [reviewed, setReviewed] = useState(false);
  const paymentSets = useSelector(selectPaymentSetsForSite());
  const payouts = useSelector(selectPayouts());
  const isLoadingPayouts = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payouts.GET_SITE_PAYOUTS_REQUEST));
  const isClosingPeriod = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, PayPeriods.UPDATE_PAY_PERIOD_STATUS_REQUEST));

  // Keyed on the dates, not the period object: closing the period replaces the object and must not reload the review.
  useEffect(() => {
    dispatch(getSitePaymentsRequest(site.id, { limit: 0, q: payPeriodDateFilters(payPeriod) }));
  }, [dispatch, site.id, payPeriod.start, payPeriod.end]);

  // Load payouts for second step.
  const goToConfirm = () => {
    dispatch(getSitePayoutsRequest(site.id, { limit: 0, q: openPayoutsInPeriod(payPeriod) }));
    setReviewed(true);
  };

  // The review stays up until the payouts are in; a failed load leaves Next to try again.
  const isReviewingPaymentSets = !reviewed || !payouts;
  const { reviewRequired, undelivered } = groupOutstandingPayouts(payouts || []);
  // The converting copy and title only apply when there is something to convert.
  const isConverting = site.settings.payPeriodClose.convertToPayroll && undelivered.length > 0;
  const layout = isReviewingPaymentSets ? 'reviewPaymentSets' : reviewRequired.length ? 'reviewRequired' : isConverting ? 'converting' : 'confirm';
  const isLoadingPayments = !paymentSets;
  const dismiss = () => onClose();

  return (
    <ModalFactory customModal isActive onClose={dismiss} closeOnOutSideClick={false}>
      <Content
        {...STEP[layout]}
        dataCy={dataCy}
        hideIcon
        centerButtons
        onLeftAction={isReviewingPaymentSets ? dismiss : () => setReviewed(false)}
        onRightAction={layout === 'reviewRequired' ? dismiss : isReviewingPaymentSets ? goToConfirm : () => dispatch(updatePayPeriodStatusRequest({ siteId: site.id, orgId, payPeriod, status: PayPeriodAction.CLOSE, onComplete: onClose }))}
        disabled={isReviewingPaymentSets && isLoadingPayments}
        isLoading={isReviewingPaymentSets ? isLoadingPayouts : isClosingPeriod}
        onClose={dismiss}
      >
        {layout === 'reviewPaymentSets' ? (
          <ReviewStep days={buildReviewDays(payPeriod, paymentSets || [], today)} isLoading={isLoadingPayments} />
        ) : (
          <ConfirmStep payouts={layout === 'reviewRequired' ? reviewRequired : undelivered} layout={layout} />
        )}
      </Content>
    </ModalFactory>
  );
};

export default ClosePayPeriodModal;
